/* app/components/MozgButton.tsx */
"use client";

import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconDefinition} from "@fortawesome/free-solid-svg-icons";
import {SizeProp} from "@fortawesome/fontawesome-svg-core";
import LocaleLink from "./LocaleLink";

/* ── new prop helpers ────────────────────────────────────────── */
type IconPosition = "left" | "right";
type TextSizeOption = "xxsmall" | "xsmall" | "small" | "medium" | "large";
type ButtonVariant = "default" | "danger" | "gray" | "primary";

interface MozgButtonProps {
  title: string;
  url?: string;
  icon?: IconDefinition;
  iconSize?: SizeProp;
  iconPosition?: IconPosition; // ⬅️ NEW
  iconGap?: string; // ⬅️ NEW (Tailwind gap-x size, default “2”)
  textSize?: TextSizeOption;
  onClick?: () => void;
  disabled?: boolean;
  variant?: ButtonVariant;
  fullWidth?: boolean; // ⬅️ NEW (“w-full” helper)
  isSubmit?: boolean;
}

/* ── helper functions for size → Tailwind classes ───────────── */
function getTextSize(size: TextSizeOption = "medium") {
  switch (size) {
    case "xxsmall":
      return "text-sm";
    case "xsmall":
      return "text-base";
    case "small":
      return "text-xl";
    case "large":
      return "text-2xl";
    default:
      return "text-xl sm:text-2xl md:text-3xl";
  }
}
function getPadding(size: TextSizeOption = "medium") {
  switch (size) {
    case "xxsmall":
      return "px-2 py-1";
    case "xsmall":
      return "px-4 py-2";
    case "small":
      return "px-4 py-2";
    case "large":
      return "px-10 py-5";
    default:
      return "px-8 py-4";
  }
}

export default function MozgButton({
  title,
  url,
  icon,
  iconSize = "xs",
  iconPosition = "left", // ⬅️ default left
  iconGap = "2",
  textSize = "medium",
  onClick,
  disabled = false,
  variant = "default",
  fullWidth = false,
  isSubmit = false
}: MozgButtonProps) {
  /* adjust icon size for tiny text */
  if (["small", "xsmall", "xxsmall"].includes(textSize)) iconSize = "sm";

  /* palette */
  let enabled = "bg-gray-800 text-white hover:bg-black hover:text-primary ";
  if (variant === "danger") enabled = "bg-gray-600 text-white hover:bg-black";
  if (variant === "gray") enabled = "bg-gray-600 text-white hover:bg-black";
  if (variant === "primary") enabled = "bg-primary text-black hover:bg-black hover:text-primary";
  const disabledStyle = "bg-gray-500 text-gray-300 cursor-not-allowed";

  /* container classes */
  const base = `
    inline-flex cursor-pointer rounded-full font-inter-display font-bold opacity-90 items-center ${fullWidth ? "w-full justify-center" : "justify-center"}
    ${getPadding(textSize)} ${getTextSize(textSize)}
    rounded-full shadow-md transform transition-all duration-300
    ${disabled ? disabledStyle : `${enabled} hover:shadow-lg hover:scale-105`}
  `;

  /* ── build inner content ── */
  const gapCls = `gap-${iconGap}`; /* tailwind gap-x helper :contentReference[oaicite:0]{index=0} */
  const iconNode = icon && <FontAwesomeIcon icon={icon} size={iconSize} className="shrink-0" />;

  const content = icon ? (
    iconPosition === "left" ? (
      <span className={`inline-flex items-center ${gapCls}`}>
        {iconNode}
        <span className="-mt-0.5">{title}</span>
      </span>
    ) : (
      <span className={`inline-flex items-center ${gapCls}`}>
        <span className="-mt-1">{title}</span>
        {iconNode}
      </span>
    )
  ) : (
    <span className="mt-0.5">{title}</span>
  );

  /* ── render as Link / span / button ── */
  if (url && !disabled) {
    return (
      <LocaleLink href={url} className={base}>
        {content}
      </LocaleLink>
    );
  }
  if (url && disabled) {
    return <span className={base}>{content}</span>;
  }
  return (
    <button type={isSubmit ? "submit" : "button"} onClick={onClick} className={base} disabled={disabled}>
      {content}
    </button>
  );
}
